import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GenerateHomeworkComponent from "./GenerateHomeworkComponent";
import CreateHomeworkComponent from "./CreateHomeworkComponent";
import UploadHomeworkComponent from "./UploadHomeworkComponent"; // <-- Import new component
import { buildApiUrl } from './config';

const HomeworkPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("upload");
  const [assignedForms, setAssignedForms] = useState([]);
  
  // ✅ NEW: Edit homework state
  const [editingHomework, setEditingHomework] = useState(null);
  const [isLoadingHomework, setIsLoadingHomework] = useState(false);

  useEffect(() => {
    // Fetch assigned forms from backend (updated for new teacher assignment structure)
    const fetchAssignedForms = async () => {
      try {
        // Add cache-busting query parameter to force fresh data
        const res = await fetch(buildApiUrl(`/teacher_assignments.json?t=${Date.now()}`));
        const formAssignments = await res.json();
        const teacherEmail = localStorage.getItem("teacherEmail");
        
        console.log("📋 Fetching assigned forms for:", teacherEmail);
        console.log("📋 Teacher assignments data:", formAssignments);
        
        // Filter forms where teacher is either an owner or teacher
        const assigned = Object.entries(formAssignments)
          .filter(([formName, assignment]) => {
            const owners = assignment.owners || [];
            const teachers = assignment.teachers || [];
            return owners.includes(teacherEmail) || teachers.includes(teacherEmail);
          })
          .map(([formName]) => formName);
          
        console.log("📋 Assigned forms for teacher:", assigned);
        setAssignedForms(assigned);
      } catch (err) {
        console.error("❌ Error fetching assigned forms:", err);
        setAssignedForms([]);
      }
    };
    fetchAssignedForms();
  }, []);

  // ✅ ADD: Check for edit parameter and fetch homework data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('edit');
    const tabParam = searchParams.get('tab');
    
    if (editId) {
      console.log(`📝 Edit mode detected for homework ID: ${editId}`);
      setIsLoadingHomework(true);
      
      // Check if homework data was passed via navigate state
      if (location.state?.assignment) {
        console.log('📋 Using homework data from navigation state');
        setEditingHomework(location.state.assignment);
        // Set tab to 'create' for editing (since that's where homework is created/edited)
        setActiveTab('create');
        setIsLoadingHomework(false);
      } else {
        // Fetch homework data from backend
        console.log('🔄 Fetching homework data from backend...');
        fetch(buildApiUrl(`/view-homework-by-id/${editId}`))
          .then(res => res.json())
          .then(homework => {
            if (homework && !homework.error) {
              setEditingHomework(homework);
              // Determine which tab to use based on homework type
              if (homework.questions && homework.questions.length > 0) {
                // If it has questions, likely was AI generated
                setActiveTab('generate');
              } else {
                // Otherwise use create tab
                setActiveTab('create');
              }
            } else {
              console.error('❌ Homework not found:', editId);
              alert('Homework not found. Redirecting to homework tools.');
              navigate('/homeworks');
            }
          })
          .catch(err => {
            console.error('❌ Error fetching homework for edit:', err);
            alert('Error loading homework for editing.');
            navigate('/homeworks');
          })
          .finally(() => {
            setIsLoadingHomework(false);
          });
      }
    } else if (tabParam && ['upload', 'create', 'generate'].includes(tabParam)) {
      // Regular tab navigation
      setActiveTab(tabParam);
      console.log(`🔗 URL tab parameter detected: ${tabParam}`);
    }
  }, [location.search, location.state, navigate]);

  const tabs = [
    { id: "upload", label: "Upload Homework", icon: "📤" },
    { id: "create", label: "Create Homework", icon: "📝" },
    { id: "generate", label: "AI Generate", icon: "🤖" }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Clear editing state when switching tabs manually
    if (editingHomework) {
      setEditingHomework(null);
      // Update URL to remove edit parameter
      navigate(`/homeworks?tab=${tabId}`, { replace: true });
    } else {
      // Update URL when tab changes
      navigate(`/homeworks?tab=${tabId}`, { replace: true });
    }
  };

  const TabButton = ({ tab, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105
        ${isActive 
          ? "bg-gradient-to-r from-blue-600 to-red-600 text-white shadow-lg shadow-blue-500/50" 
          : "bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-yellow-100 hover:to-blue-100 border border-yellow-200/50"
        }
      `}
    >
      <span className="text-xl">{tab.icon}</span>
      <span className="text-sm">{tab.label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-red-50 relative overflow-hidden">
      {/* School Stationary Pattern Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-20 left-20 w-16 h-2 bg-blue-400 rounded transform rotate-45"></div>
        <div className="absolute top-40 right-32 w-12 h-2 bg-red-400 rounded transform -rotate-12"></div>
        <div className="absolute bottom-32 left-40 w-20 h-2 bg-yellow-400 rounded transform rotate-12"></div>
        <div className="absolute top-60 left-60 w-14 h-2 bg-green-400 rounded transform -rotate-6"></div>
        <div className="absolute bottom-40 right-40 w-18 h-2 bg-orange-400 rounded transform rotate-45"></div>
        <div className="absolute top-1/2 left-1/4 w-10 h-2 bg-purple-400 rounded transform -rotate-45"></div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-6 relative z-10">
        {/* Header */}
        <div className="mb-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-yellow-200/50">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/teacher?module=assignments')}
              className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 rounded-xl flex items-center justify-center shadow-md transition-all duration-300 group border border-blue-300"
              title="Back to Assignments"
            >
              <svg className="w-6 h-6 text-blue-600 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform duration-300">
              <span className="text-white text-2xl">
                {editingHomework ? '✏️' : '📚'}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 via-red-600 to-yellow-600 bg-clip-text text-transparent">
                {editingHomework ? 'Edit Homework' : 'Homework Mission Control'}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {editingHomework 
                  ? `Editing: ${editingHomework.title || 'Untitled Homework'}`
                  : 'Create, upload, and manage homework assignments ✨'
                }
              </p>
            </div>
          </div>
          {isLoadingHomework && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-yellow-50 border-2 border-yellow-300 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-blue-700 font-medium">Loading homework data...</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
            />
          ))}
        </div>

        {/* Content */}
        <div className="transition-all duration-300 ease-in-out">
          {activeTab === "upload" && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-yellow-200/50 p-6">
              <UploadHomeworkComponent assignedForms={assignedForms} />
            </div>
          )}
          {activeTab === "create" && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-blue-200/50 p-6">
              <CreateHomeworkComponent 
                assignedForms={assignedForms} 
                editingHomework={editingHomework}
                onEditComplete={() => {
                  setEditingHomework(null);
                  navigate('/teacher?mainTab=assignments&tab=my-assignments&module=assignments', { replace: true });
                }}
              />
            </div>
          )}
          {activeTab === "generate" && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-purple-200/50 p-6">
              <GenerateHomeworkComponent
                availableForms={assignedForms}
                teacherName={localStorage.getItem("teacherEmail") || "Teacher"}
                editingHomework={editingHomework}
                onEditComplete={() => {
                  setEditingHomework(null);
                  navigate('/teacher?mainTab=assignments&tab=my-assignments&module=assignments', { replace: true });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeworkPage;
